import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { GeometrixLoading } from './components/GeometrixLoading';
import { AppHeader } from './components/AppHeader';
import firebase from './firebase';
const Home = React.lazy(() => import('./pages/Home'));
const EditorPage = React.lazy(() => import('./pages/EditorPage'));
const ReadPage = React.lazy(() => import('./pages/ReadPage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const Support = React.lazy(() => import('./pages/Support'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const FirebaseAuthUI = React.lazy(() => import('./FirebaseAuthUI'));

const EMULATOR_HOST = process.env.EMULATOR_HOST || '127.0.0.1';
const backendUrl = process.env.NODE_ENV === 'development'
  ? `http://${EMULATOR_HOST}:5001/geometrix-backend/us-central1`
  : 'https://us-central1-geometrix-backend.cloudfunctions.net';

export default function App() {
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Detect if running inside a WebView (basic check)
  const isWebView = /wv|WebView|; wv\)/i.test(window.navigator.userAgent) || window.navigator.userAgent.includes('GeometrixUniverse');
  const [webViewForceLogin, setWebViewForceLogin] = useState(false);


  useEffect(() => {
    const unsub = firebase.auth().onAuthStateChanged((user: any) => {
      setUser(user);
      if (user) {
        setWebViewForceLogin(false);
        // If on /login, always redirect to home after sign-in
        if (location.pathname === "/login") {
          navigate("/home", { replace: true });
        } else {
          // If coming from a protected route, redirect back after login
          const from = (location.state && (location.state as any).from?.pathname) || "/";
          navigate(from, { replace: true });
        }
      }
    });
    return () => unsub();
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSO session bootstrap: if no Firebase user, call /authBootstrap
  const [bootstrapping, setBootstrapping] = useState(false);
  useEffect(() => {
    const session = window.localStorage.getItem("session");
    if (!user && !bootstrapping && session) {
      setBootstrapping(true);
      fetch(`${backendUrl}/authBootstrap`, {
        credentials: 'include',
        headers: { "X-Debug-Cookie": session },
      })
        .then(async res => {
          return res.json();
        })
        .then(async ({ customToken }) => {
          if (customToken) {
            await firebase.auth().signInWithCustomToken(customToken);
          }
        })
        .catch(() => {
          setWebViewForceLogin(isWebView); // Only force login if in WebView
          alert("SSO session expired, please log in again.");
        })
        .finally(() => setBootstrapping(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (bootstrapping) {
    return <GeometrixLoading />;
  }

  if (webViewForceLogin) {
    return (
      <div className="container">
        <div style={{ maxWidth: 400, margin: '40px auto' }}>
          <FirebaseAuthUI />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {!isWebView && <AppHeader user={user} />}
      <Suspense fallback={<GeometrixLoading />}>
        <Routes>
          <Route path="/support" element={<Support />} />
          <Route path="/termsofuse" element={<TermsOfUse />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/home" state={{ from: location }} replace />} />
          <Route path="/login" element={
            user ? <Navigate to={(location.state && (location.state as any).from?.pathname) || "/home"} replace /> : (
              <div style={{ maxWidth: 400, margin: '40px auto' }}>
                <FirebaseAuthUI />
              </div>
            )
          } />
          <Route path="/home" element={user ? <Home /> : <Navigate to="/login" state={{ from: location }} replace />} />
          <Route path="/new" element={user ? <EditorPage /> : <Navigate to="/login" state={{ from: location }} replace />} />
          <Route path="/edit/:id" element={user ? <EditorPage /> : <Navigate to="/login" state={{ from: location }} replace />} />
          <Route path="/p/:slug" element={user ? <ReadPage /> : <Navigate to="/login" state={{ from: location }} replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}
